import React, { useState, useEffect } from 'react';
import { Btn } from '../components/Button';
import { Sec, SH } from '../components/Section';
import { Orbs, Grid } from '../components/Orbs';
import { C, F, IMAGES } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';

export const PageHero = ({ eyebrow, title, accent, subtitle, body, cta, ctaLink, onCtaClick, cta2, cta2Link, stats, image, children, compact, twoColumn }) => {
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Use compact mode for pages without visuals (no image/children)
  const hasVisuals = image || children;
  // twoColumn forces 2-column layout even without visuals (for persona pages with placeholder)
  const useTwoColumn = twoColumn || hasVisuals;

  return (
    <Sec
      dark={theme === 'light'}
      style={{
        minHeight: 'auto',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '120px',
        paddingBottom: '40px'
      }}
    >
      <Orbs />
      <Grid o={0.03} />

      <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%', position: 'relative', zIndex: 1, padding: '0 20px' }}>
        <div
          className="hero-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : (useTwoColumn ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr'),
            gap: '48px',
            alignItems: 'center'
          }}
        >
          {/* LHS - Text content: order 1 on mobile (always first) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ order: isMobile ? 1 : 0 }}
          >
            {eyebrow && (
              <div
                style={{
                  fontSize: 'clamp(11px, 2vw, 14px)',
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: C.secondary,
                  marginBottom: '20px'
                }}
              >
                {eyebrow}
              </div>
            )}

            <h1
              style={{
                fontFamily: F,
                fontSize: 'clamp(32px, 5vw, 56px)',
                fontWeight: 700,
                lineHeight: 1.1,
                marginBottom: '20px'
              }}
            >
              {title}
              {accent && (
                <>
                  {' '}
                  <span style={{ background: C.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {accent}
                  </span>
                </>
              )}
            </h1>

            {subtitle && (
              <p
                className="font-[Manrope]"
                style={{
                  fontSize: 'clamp(16px, 2.5vw, 20px)',
                  fontWeight: 600,
                  lineHeight: 1.4,
                  opacity: 0.9,
                  marginBottom: '14px',
                  maxWidth: '500px'
                }}
              >
                {subtitle}
              </p>
            )}

            {body && (
              <p
                className="font-[Manrope]"
                style={{
                  fontSize: 'clamp(14px, 2vw, 16px)',
                  lineHeight: 1.7,
                  opacity: 0.75,
                  marginBottom: '28px',
                  maxWidth: '500px'
                }}
              >
                {body}
              </p>
            )}

            {(cta || cta2) && (
              <div style={{ 
                display: 'flex', 
                gap: 'clamp(12px, 3vw, 16px)', 
                flexWrap: 'wrap',
                width: '100%'
              }}>
                {cta && (
                  <Btn 
                    variant="primary" 
                    large 
                    to={onCtaClick ? undefined : (ctaLink || '/score')} 
                    onClick={onCtaClick}
                    data-testid="hero-cta-btn"
                  >
                    {cta}
                  </Btn>
                )}
                {cta2 && (
                  <Btn variant="outline" large to={cta2Link || '/platform'} data-testid="hero-cta2-btn">
                    {cta2}
                  </Btn>
                )}
              </div>
            )}

            {stats && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: 'clamp(16px, 4vw, 32px)',
                  marginTop: 'clamp(32px, 6vw, 64px)'
                }}
              >
                {stats.map((stat, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 700, fontFamily: F, color: C.secondary, marginBottom: '8px' }}>
                      {stat.value}
                    </div>
                    <div className="font-[Manrope]" style={{ fontSize: 'clamp(12px, 2vw, 14px)', opacity: 0.7 }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </motion.div>

          {/* RHS Column - order 2 on mobile (always second) */}
          {(image || children || twoColumn) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                order: isMobile ? 2 : 0
              }}
            >
              {image ? (
                <div
                  style={{
                    borderRadius: '24px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: C.neon
                  }}
                >
                  <img
                    src={image}
                    alt="Hero"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
              ) : children ? (
                children
              ) : null}
            </motion.div>
          )}
        </div>
      </div>
    </Sec>
  );
};

export const BottomCTA = ({ title, accent, subtitle, ctaText, ctaLink, onCtaClick, cta2Text, cta2Link }) => {
  const { theme } = useTheme();

  return (
    <Sec dark={theme === 'light'} style={{ paddingTop: '120px', paddingBottom: '120px' }}>
      <Orbs />
      <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2
            style={{
              fontFamily: F,
              fontSize: 'clamp(32px, 6vw, 56px)',
              fontWeight: 700,
              lineHeight: 1.2,
              marginBottom: '24px'
            }}
          >
            {title}
            {accent && (
              <>
                {' '}
                <span style={{ background: C.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {accent}
                </span>
              </>
            )}
          </h2>
          <p
            className="font-[Manrope]"
            style={{
              fontSize: '20px',
              lineHeight: 1.6,
              opacity: 0.8,
              marginBottom: '40px'
            }}
          >
            {subtitle}
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Btn 
              variant="primary" 
              large 
              to={onCtaClick ? undefined : (ctaLink || '/score')} 
              onClick={onCtaClick}
              data-testid="bottom-cta-btn"
            >
              {ctaText}
            </Btn>
            {cta2Text && (
              <Btn variant="outline" large to={cta2Link || '/platform'} data-testid="bottom-cta2-btn">
                {cta2Text}
              </Btn>
            )}
          </div>
        </motion.div>
      </div>
    </Sec>
  );
};